import clsx from "clsx";
import { BACKEND } from "core/consts";
import { tokenAccessor } from "core/model/api/transport";
import { getBackendConfig } from "core/model/config";
import { renderAsync } from "docx-preview";
import { Spinner } from "ds/ui";
import type { PdfViewerFile } from "dsl/atoms/PDFViewer";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "translations";

export const DocxViewer = ({
  className,
  isLoadingPdf,
  selectedFile,
}: {
  className?: string;
  selectedFile: PdfViewerFile;
  isLoadingPdf?: boolean;
}) => {
  const translations = useTranslations();
  const aiCoreConfig = getBackendConfig(BACKEND.AI_CORE);
  const token = tokenAccessor();
  const containerRef = useRef<HTMLDivElement>(null);
  const [loadError, setLoadError] = useState(false);
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    if (isLoadingPdf || !selectedFile?.url) return;

    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;

    (async () => {
      setLoadError(false);
      setRendering(true);
      container.innerHTML = "";

      try {
        const isAiCoreHostedFile =
          aiCoreConfig?.host && selectedFile.url.includes(aiCoreConfig.host);
        const response = await fetch(selectedFile.url, {
          headers:
            isAiCoreHostedFile && token
              ? { Authorization: `Bearer ${token}` }
              : undefined,
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        if (cancelled) return;
        await renderAsync(arrayBuffer, container, undefined, {
          className: "docx",
          ignoreWidth: true,
          inWrapper: false,
        });
      } catch (error) {
        console.error("DOCX load error:", error);
        if (!cancelled) {
          setLoadError(true);
        }
      } finally {
        if (!cancelled) {
          setRendering(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      container.innerHTML = "";
    };
  }, [selectedFile.url, isLoadingPdf, aiCoreConfig?.host, token]);

  if (!selectedFile.url) return null;

  const showOverlay = isLoadingPdf || rendering;

  return (
    <div
      className={clsx(
        "relative flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-x-auto overflow-y-auto rounded-[2px] border border-gray-400 bg-white shadow-lg",
        className,
      )}
      data-testid="docx-viewer"
      style={{ scrollbarGutter: "stable" }}
    >
      {showOverlay ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
          <Spinner
            color="primary"
            size="lg"
            testId="spinner-loading-docx-document"
          />
        </div>
      ) : null}
      {loadError ? (
        <p className="p-4">
          {translations.extractDocs.docsCreation.errorDocxLoadingFailed}
        </p>
      ) : null}
      <div
        ref={containerRef}
        className={clsx(
          "docx-preview-root min-h-[200px] max-w-full min-w-0 flex-1 overflow-x-auto overflow-y-auto bg-white",
          "[&_section.docx]:box-border [&_section.docx]:max-w-full [&_section.docx]:bg-white [&_section.docx]:shadow-sm",
          { hidden: loadError },
        )}
      />
    </div>
  );
};
